import { useEffect, useRef, useState } from 'react';
import * as VKID from '@vkid/sdk';
import { Box, CircularProgress } from '@mui/material';
import { authApi } from '../api/auth';
import { useAuthStore } from '../store/authStore';

interface VKAuthProps {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export default function VKAuth({ onSuccess, onError }: VKAuthProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { setToken, fetchUser } = useAuthStore();
  const initializingRef = useRef(false);
  const processingRef = useRef(false);
  const [isProcessingCode, setIsProcessingCode] = useState(false);

  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    const processAuth = async () => {
      try {
        // Initialize VK ID SDK
        VKID.Config.init({
          app: parseInt(import.meta.env.VITE_VK_APP_ID || '54445519'),
          redirectUrl: window.location.origin,
          state: 'vkid_auth',
        });

        // Check for code in URL (after redirect from VK)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const deviceId = urlParams.get('device_id');
        
        if (code && deviceId && !processingRef.current) {
          processingRef.current = true;
          setIsProcessingCode(true);
          console.log('🔄 Found VK code in URL, exchanging for token');
          
          try {
            // Exchange code for token
            const tokenData = await VKID.Auth.exchangeCode(code, deviceId);
            console.log('✅ Token data from SDK:', tokenData);
            
            // Send to backend
            const result = await authApi.loginWithVKID({
              token: tokenData.access_token,
              uuid: String(tokenData.user_id) || deviceId
            });

            console.log('✅ Backend response:', result);

            // Save JWT token
            setToken(result.access_token);
            
            // Fetch user data
            await fetchUser();
            console.log('✅ User data fetched');

            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            
            setIsProcessingCode(false);
            onSuccess?.();
          } catch (error) {
            console.error('❌ Token exchange error:', error);
            window.history.replaceState({}, document.title, window.location.pathname);
            setIsProcessingCode(false);
            onError?.(error);
          }
          
          return;
        }

        // Only render button if not processing code
        if (!containerRef.current || isProcessingCode) return;

        // Create OneTap button instance
        const oneTap = new VKID.OneTap();

        // Render button
        oneTap
          .render({
            container: containerRef.current,
            scheme: VKID.Scheme.LIGHT,
            lang: VKID.Languages.RUS,
            showAlternativeLogin: false,
            fastAuthEnabled: true,
            styles: {
              borderRadius: 8,
            },
          })
          .on('error' as any, (error: any) => {
            console.error('❌ VK ID Error:', error);
            onError?.(error);
          })
          .on('success' as any, async (payload: any) => {
            try {
              console.log('✅ VK ID success payload:', payload);
              
              // If there's a code - exchange it for token via SDK
              if (payload.code && payload.device_id) {
                console.log('🔄 Exchanging code for token via SDK');
                const tokenData = await VKID.Auth.exchangeCode(payload.code, payload.device_id);
                console.log('✅ Token data from SDK:', tokenData);
                
                // Send to backend
                const result = await authApi.loginWithVKID({
                  token: tokenData.access_token,
                  uuid: String(tokenData.user_id) || payload.device_id
                });

                // Save JWT token
                setToken(result.access_token);
                
                // Fetch user data
                await fetchUser();

                // Clean URL
                window.history.replaceState({}, document.title, window.location.pathname);
                
                onSuccess?.();
              }
              // If there's already a token
              else if (payload.token || payload.access_token) {
                const access = payload.token || payload.access_token;
                
                const result = await authApi.loginWithVKID({
                  token: access,
                  uuid: payload.uuid || payload.device_id
                });

                setToken(result.access_token);
                await fetchUser();
                onSuccess?.();
              } else {
                throw new Error('No valid auth data in payload');
              }
            } catch (error) {
              console.error('❌ Backend auth error:', error);
              onError?.(error);
            }
          });
      } catch (error) {
        console.error('Ошибка инициализации VK ID:', error);
        onError?.(error);
        initializingRef.current = false;
      }
    };

    processAuth();

    // Cleanup
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      initializingRef.current = false;
    };
  }, [setToken, fetchUser, onSuccess, onError]);

  return (
    <Box 
      ref={containerRef}
      sx={{
        minHeight: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isProcessingCode && <CircularProgress size={24} />}
    </Box>
  );
}

